/**
 * 麦克风权限自动处理 — 不再弹自家"需要授权" Modal, 而是借用户首次交互的 user gesture
 * 直接调用 navigator.mediaDevices.getUserMedia, 触发**浏览器原生权限气泡**.
 *
 * 浏览器规则: getUserMedia 必须在 user gesture (click/keydown/touch) 内调用, 否则不弹.
 * 因此挂载时只能注册 once 监听器, 在用户第一次点击页面任意位置时调一次, 让浏览器自己弹.
 *
 * 三种状态处理:
 *   - granted  已授权 → 静默, 不渲染
 *   - prompt   尚未询问 → 注册 once click 监听, 用户首次点击即触发原生气泡; 本组件不渲染 Modal
 *   - denied   已拒绝, 无法再 prompt → 渲染我们自己的指引 Modal (告诉用户如何在浏览器设置里手动改)
 *   - 浏览器无 mediaDevices → 走 denied 路径展示指引
 *
 * 同会话节流: 用户点 "知道了"/"稍后" 后 sessionStorage 标记, 同会话不再自动弹指引 Modal.
 */
import { AudioMutedOutlined, AudioOutlined, WarningOutlined } from '@ant-design/icons';
import { Alert, Button, Divider, Modal, Space, Typography, message } from 'antd';
import { useCallback, useEffect, useState } from 'react';

type MicState = 'unknown' | 'granted' | 'denied' | 'prompt';

const DISMISS_KEY = 'sip_mic_prompt_dismissed';

export function MicPermissionGate() {
  const [micState, setMicState] = useState<MicState>('unknown');
  const [open, setOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);
  /** 用户在 Modal 内点过"一键授权"后, 浏览器仍 reject — 提示用走手动步骤 */
  const [retryRejected, setRetryRejected] = useState(false);

  /** 立即调用 getUserMedia 触发浏览器原生气泡; 必须在 user gesture 内 */
  const fireNativePrompt = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicState('granted');
      message.success('麦克风已授权');
    } catch {
      // 用户拒绝 / 已经 denied — onchange 监听会捕获新状态
    }
  }, []);

  /** Modal 内"一键授权"按钮 — 在 click 这个 user gesture 内尝试 getUserMedia */
  const handleOneClickGrant = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      message.error('当前浏览器不支持 getUserMedia, 请使用 Chrome / Edge / Safari 最新版');
      return;
    }
    setRequesting(true);
    setRetryRejected(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicState('granted');
      setOpen(false);
      sessionStorage.removeItem(DISMISS_KEY);
      message.success('麦克风已授权, 可以拨打 VoIP 电话了');
    } catch (err) {
      // denied 状态下 getUserMedia 会被浏览器静默 reject; prompt 状态用户点了拒绝也会 reject
      const name = (err as { name?: string })?.name;
      const msg = err instanceof Error ? err.message : String(err);
      setRetryRejected(true);
      if (name === 'NotAllowedError') {
        message.warning('浏览器拒绝了授权请求 — 请按下方步骤手动开启');
      } else if (name === 'NotFoundError') {
        message.error('未检测到麦克风设备');
      } else {
        message.error(`授权失败: ${msg}`);
      }
    } finally {
      setRequesting(false);
    }
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;

    // 浏览器完全不支持 getUserMedia (老 Safari / 非 https) — 直接走 denied 指引
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicState('denied');
      if (sessionStorage.getItem(DISMISS_KEY) !== '1') setOpen(true);
      return;
    }

    // Permissions API 不支持 (Safari < 16) — 用 once click 注册原生气泡触发
    if (!navigator.permissions) {
      setMicState('prompt');
      const once = () => {
        document.removeEventListener('click', once, true);
        fireNativePrompt();
      };
      document.addEventListener('click', once, { once: true, capture: true });
      return;
    }

    let cleanupClick: (() => void) | null = null;

    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((status) => {
        const apply = (next: MicState) => {
          setMicState(next);
          if (next === 'granted') {
            setOpen(false);
            sessionStorage.removeItem(DISMISS_KEY);
            if (cleanupClick) cleanupClick();
          } else if (next === 'denied') {
            // 用户曾拒绝, 无法直接再弹原生气泡 — 弹我们自己的指引 (节流)
            if (sessionStorage.getItem(DISMISS_KEY) !== '1') setOpen(true);
            if (cleanupClick) cleanupClick();
          } else if (next === 'prompt') {
            // 首次访问, 等用户第一次 click 触发原生气泡
            const once = () => {
              document.removeEventListener('click', once, true);
              fireNativePrompt();
            };
            document.addEventListener('click', once, { once: true, capture: true });
            cleanupClick = () => document.removeEventListener('click', once, true);
          }
        };
        apply(status.state as MicState);
        status.onchange = () => apply(status.state as MicState);
      })
      .catch(() => {
        // Permissions API 抛错 (Firefox 部分版本) — fallback 走 once click
        setMicState('prompt');
        const once = () => {
          document.removeEventListener('click', once, true);
          fireNativePrompt();
        };
        document.addEventListener('click', once, { once: true, capture: true });
      });

    return () => {
      if (cleanupClick) cleanupClick();
    };
  }, [fireNativePrompt]);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setOpen(false);
  };

  // 仅 denied 状态会展示我们自己的指引 Modal
  if (micState !== 'denied') return null;

  return (
    <Modal
      title={
        <Space>
          <WarningOutlined style={{ color: '#faad14' }} />
          麦克风权限被禁用
        </Space>
      }
      open={open}
      onCancel={handleDismiss}
      maskClosable={false}
      footer={null}
      width={460}
    >
      <Typography.Paragraph style={{ marginBottom: 12 }}>
        VoIP 拨号 / 接听 / 会议都需要使用您的麦克风. 先尝试一键授权:
      </Typography.Paragraph>

      <Button
        type="primary"
        size="large"
        block
        icon={<AudioOutlined />}
        loading={requesting}
        onClick={handleOneClickGrant}
        style={{ marginBottom: 12, height: 44, fontSize: 15 }}
      >
        一键授权麦克风
      </Button>

      {retryRejected && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="浏览器拒绝了一键授权请求"
          description="您之前在此站点点过「拒绝」, 浏览器不允许程序再次弹出原生授权气泡. 请按下方手动步骤开启."
        />
      )}

      <Divider style={{ margin: '8px 0' }}>手动开启方式</Divider>

      <Typography.Paragraph style={{ marginBottom: 4 }}>
        <strong>Chrome / Edge:</strong>
      </Typography.Paragraph>
      <ol style={{ paddingLeft: 18, marginTop: 0, lineHeight: 1.8 }}>
        <li>
          点击地址栏左侧 <AudioMutedOutlined style={{ color: '#ff4d4f' }} /> 或 🔒 图标
        </li>
        <li>找到 "麦克风" 设置, 改为 "允许"</li>
        <li>关闭弹层后刷新页面 (Cmd/Ctrl + R)</li>
      </ol>
      <Typography.Paragraph style={{ marginBottom: 4, marginTop: 12 }}>
        <strong>Safari:</strong>
      </Typography.Paragraph>
      <ol style={{ paddingLeft: 18, marginTop: 0, lineHeight: 1.8 }}>
        <li>菜单栏 Safari → 偏好设置 → 网站 → 麦克风</li>
        <li>找到本站点, 改为 "允许"</li>
        <li>刷新页面</li>
      </ol>
      <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 12 }}>
        <Button onClick={handleDismiss}>稍后</Button>
        <Button type="default" onClick={() => window.location.reload()}>
          已修改设置, 刷新页面
        </Button>
      </Space>
    </Modal>
  );
}
